<template>
    <div>
        <v-alert
            v-if="alert.show"
            :type="alert.type"
            dismissible
            class="position-fixed top-0 end-0 m-4"
            style="z-index: 9999; min-width: 300px"
            @input="alert.show = false"
        >
            {{ alert.message }}
        </v-alert>

        <!-- Wrapper to maintain id="devis-overlay" for external CSS/JS control -->
        <div id="devis-overlay">
            <CustomDialog
                :value="true"
                :defaultTitle="'Devis N° ' + order"
                :isInPopup="false"
                width="90%"
                heightClass="ppv2-max-h-[90vh] !ppv2-max-w-[1400px]"
                @cancel="close"
            >
                <!-- Hide default actions -->
                <template #actions>
                    <span></span>
                </template>

                <template #content>
                    <div class="template-selector ppv2-mb-4 ppv2-rounded-lg ppv2-p-4">
                        <div class="selector-label">
                            <span v-if="quote">{{ $t('checkout.devis.change_template') }}</span>
                            <span v-else>{{ $t('checkout.devis.add_template') }}</span> {{ $t('checkout.devis.template_model') }}
                        </div>

                        <div class="selector-dropdown custom-select-wrapper">
                            <select v-model="selectedTemplateId" class="custom-select">
                                <option value="-1">{{ $t('checkout.devis.no_template') }}</option>
                                <option v-for="t in templates" :key="t.id" :value="t.id">{{ t.title }}</option>
                            </select>
                        </div>

                        <div class="selector-due-date" style="display: flex; align-items: center; gap: 8px; margin-top: 8px">
                            <label style="font-size: 13px; font-weight: 500; white-space: nowrap">{{
                                $t('checkout.devis.due_date')
                            }}</label>
                            <input
                                type="date"
                                v-model="dueDateInput"
                                :min="orderCreatedAtMin"
                                @change="saveDueDate"
                                class="custom-select"
                                style="max-width: 180px; cursor: pointer; width: 100%"
                                onclick="this.showPicker()"
                            />
                            <span v-if="dueDateSaving" style="font-size: 12px; color: #888">{{
                                $t('checkout.devis.saving')
                            }}</span>
                        </div>

                        <div class="selector-button text-end p-1">
                            <button
                                @click="openBuilderAuto"
                                class="btn-signature"
                                :disabled="builderLoading"
                                :aria-busy="builderLoading && builderMode === 'auto'"
                            >
                                <template v-if="builderLoading && builderMode === 'auto'">
                                    <svg class="spinner" viewBox="0 0 50 50" aria-hidden="true">
                                        <circle class="path" cx="25" cy="25" r="20" fill="none" stroke-width="4"></circle>
                                    </svg>
                                    <span>{{ $t('checkout.devis.loading') }}</span>
                                </template>
                                <template v-else>
                                    <span>{{ $t('checkout.devis.configure_signature') }}</span>
                                </template>
                            </button>
                            <button @click="saveTemplate" class="btn-save">
                                <span v-if="quote">{{ $t('checkout.devis.save') }}</span>
                                <span v-else>{{ $t('checkout.devis.generate') }}</span>
                            </button>
                            <button v-if="quote" @click="sentQuote" class="btn-save">{{ $t('checkout.devis.send') }}</button>
                            <button v-if="quote" @click="validateQuote" class="btn-validate" :disabled="validating">
                                <span v-if="validating">{{ $t('checkout.devis.validating') }}</span>
                                <span v-else>{{ $t('checkout.devis.validate') }}</span>
                            </button>
                        </div>
                    </div>

                    <div style="height: 65vh">
                        <iframe title="PDF" width="100%" height="100%" :src="documentUrl" style="border: none"></iframe>
                    </div>
                </template>
            </CustomDialog>
        </div>

        <!-- Modal Builder -->
        <div v-if="showBuilder" class="modal-overlay" style="z-index: 99999">
            <div class="modal-card">
                <div class="modal-head">
                    <h3>{{ $t('checkout.devis.prepare_signature') }}</h3>
                    <button class="btn-close-modal" @click="showBuilder = false">✕</button>
                </div>

                <docuseal-builder
                    v-if="builderToken"
                    key="1635296"
                    :data-token="builderToken"
                    data-language="fr"
                    data-field-types="signature,date,initials,text"
                    data-draw-field-type="signature"
                    style="height: 80vh; width: 100%; display: block"
                    :data-submitters="builderSubmitters"
                ></docuseal-builder>
            </div>
        </div>
    </div>
</template>

<script>
import ApiManager from './_core/ApiManager'
import CustomDialog from '../../Shared/CustomDialog.vue'

export default {
    name: 'DevisSigner',
    components: { CustomDialog },
    props: ['order'],
    data() {
        return {
            documentUrl: '',
            version: 0,
            selectedTemplateId: -1,
            quote: null,
            templates: [],
            alert: { show: false, message: 'alert', type: 'success' },
            alertTimeoutId: null,
            dueDateInput: '',
            dueDateSaving: false,

            // DocuSeal state
            showBuilder: false,
            builderToken: '',
            builderKey: 0,
            builderLoading: false,
            builderMode: null, // 'auto' | 'manual'
            builderOpId: 0, // anti-race
            builderTemplateId: null,

            signingSrc: '',
            signerEmail: '',
            docusealLoaded: false,
            validating: false,
        }
    },

    computed: {
        orderCreatedAtMin() {
            const raw = this.quote?.order?.created_at
            if (!raw) return ''
            const d = new Date(raw)
            d.setDate(d.getDate() + 1)
            return d.toISOString().substring(0, 10)
        },
        defaultRecipientEmail() {
            return this.signerEmail || this.quote?.order?.user?.email || ''
        },
        builderSubmitters() {
            const email = (this.defaultRecipientEmail || '').trim()
            if (!email) return '[]' // aucun pré-remplissage si pas d'email
            return JSON.stringify([{ email, role: 'Client', name: this.quote?.client?.name || '' }])
        },
    },

    methods: {
        init() {
            if (!this.order || this.order === 0) {
                console.warn('Devis: Invalid order ID, skipping initialization')
                return
            }
            this.buildDocumentURL()
            this.getQuote()
            this.getTemplates()
        },

        close() {
            const devisComponent = document.getElementById('devis-overlay')
            if (devisComponent) devisComponent.classList.remove('devis-show')
            this.version++
            this.buildDocumentURL()
        },

        buildDocumentURL() {
            if (!this.order || this.order === 0) return
            this.documentUrl = '/back-office/devis/' + this.order + '?v=' + this.version
        },

        getDocumentURL() {
            if (!this.order || this.order === 0) return ''
            return '/back-office/devis/' + this.order + '?v=' + this.version
        },

        async getTemplates() {
            try {
                const res = await ApiManager.getAllQuotesTemplates()
                this.templates = Array.isArray(res.data) ? res.data : []
            } catch (error) {
                this.$toast.error(error.response.data.message)
            }
        },

        async getAppliedTemplate() {
            this.selectedTemplateId = this.quote ? this.quote.template_id ?? -1 : -1
        },

        async getQuote() {
            if (!this.order || this.order === 0) {
                console.warn('Devis: Invalid order ID, skipping getQuote')
                return
            }
            try {
                const res = await ApiManager.getQuote(this.order, { customer: true })
                this.quote = res.data.quote
                this.signerEmail = this.quote?.order?.user?.email || ''
                const rawDueDate = this.quote?.order?.due_date
                this.dueDateInput = rawDueDate ? rawDueDate.substring(0, 10) : ''
            } catch (error) {
                this.$toast.error(error.response.data.message)
                this.selectedTemplateId = -1
                this.quote = null
            } finally {
                this.getAppliedTemplate()
            }
        },
        async saveDueDate() {
            if (!this.order || this.order === 0) return
            this.dueDateSaving = true
            try {
                await ApiManager.updateDueDate(this.order, this.dueDateInput || null)
                this.showMessage("Date d'échéance mise à jour.", 'success')
                this.version++
                this.buildDocumentURL()
            } catch (error) {
                this.$toast.error(error.response.data.message)
                const msg =
                    error?.response?.data?.message ||
                    error?.response?.data?.error ||
                    "Erreur lors de la mise à jour de la date d'échéance."
                this.showMessage(msg, 'error')
            } finally {
                this.dueDateSaving = false
            }
        },
        async saveTemplate() {
            if (!this.order || this.order === 0) return
            try {
                const res = await ApiManager.saveQuoteTemplate(
                    this.order,
                    this.selectedTemplateId == -1 ? null : this.selectedTemplateId
                )
                if (res.data.updated_count) {
                    this.showMessage('Template mis à jour avec succès!', 'success')
                    this.version++
                    this.buildDocumentURL()
                    this.getQuote()
                } else {
                    this.showMessage('Aucune donnée reçue du serveur.', 'error')
                }
            } catch (error) {
                this.$toast.error(error.response.data.message)
                console.error('Erreur lors de la mise à jour du template:', error)
                //this.showMessage('Une erreur est survenue lors de la mise à jour du template.', 'error')
            }
        },
        async validateQuote() {
            if (!this.order || this.order === 0) return
            this.validating = true
            try {
                await ApiManager.validateQuote(this.order)
                this.showMessage('Devis validé avec succès.', 'success')
                this.close()
                window.dispatchEvent(new CustomEvent('open-checkout-overlay'))
            } catch (error) {
                this.$toast.error(error.response.data.message)
                //this.showMessage('Une erreur est survenue lors de la validation du devis.', 'error')
            } finally {
                this.validating = false
            }
        },
        async sentQuote() {
            if (!this.order || this.order === 0) return
            try {
                await ApiManager.sentQuote(this.order)
                this.showMessage("Email programmé pour l'envoi avec succès.", 'success')
            } catch (error) {
                this.$toast.error(error.response.data.message)
                //this.showMessage("Une erreur est survenue lors de l'envoi de l'email.", 'error')
            }
        },

        // Loader DocuSeal
        async ensureDocuSealScripts() {
            if (this.docusealLoaded) return
            await this.loadScript('https://cdn.docuseal.com/js/builder.js')
            await this.loadScript('https://cdn.docuseal.com/js/form.js')
            this.docusealLoaded = true
        },
        loadScript(src) {
            return new Promise((resolve, reject) => {
                if (document.querySelector(`script[src="${src}"]`)) return resolve()
                const s = document.createElement('script')
                s.src = src
                s.onload = resolve
                s.onerror = reject
                document.body.appendChild(s)
            })
        },

        // Anti-conflit: démarrage d'une opération builder
        async startBuilderOp(mode) {
            this.builderMode = mode
            this.builderLoading = true
            this.builderOpId += 1
            const myOp = this.builderOpId

            // purge l'état précédent
            this.showBuilder = false
            this.builderToken = ''
            this.builderTemplateId = null
            this.builderKey++ // reconstruit le webcomponent
            await this.$nextTick()

            return myOp
        },

        // Anti-conflit: terminer proprement si c'est encore l'op en cours
        async finishBuilderOp(myOp, { token, templateId }) {
            if (myOp !== this.builderOpId) return // une autre op a pris la main
            this.builderTemplateId = templateId
            this.builderToken = token
            this.builderKey++
            this.showBuilder = true
            this.builderLoading = false
        },

        // Parcours AUTO: upsert template (signature en bas) puis ouvre ce template
        async openBuilderAuto() {
            try {
                await this.ensureDocuSealScripts()
                const myOp = await this.startBuilderOp('auto')

                // 1) Upsert template avec champ déjà positionné (coords normalisées 0..1)
                const pdfUrl = this.getDocumentURL()
                const upsertPayload = {
                    order_id: this.order,
                    pdf_url: pdfUrl,
                    role: 'Client',
                    x: 0.08,
                    y: 0.88,
                    w: 0.35,
                    h: 0.06, // bas de page
                    page: 'last', // converti côté backend
                }
                const upsertRes = await ApiManager.upsertDocuSealTemplate(upsertPayload)
                this.signerEmail = upsertRes?.data?.customer?.email || ''
                const tplId = upsertRes?.data?.template_id || upsertRes?.data?.id || upsertRes?.data?.template?.id

                if (!tplId) throw new Error('Template DocuSeal introuvable après upsert')

                this.selectedTemplateId = tplId

                // Token builder MINIMAL pour CE template (nouvelle méthode)
                const { data } = await ApiManager.getDocuSealBuilderTokenByTemplateId(tplId)
                this.builderTemplateId = data.template_id // pour affichage debug (voir + bas)
                await this.finishBuilderOp(myOp, { token: data.token, templateId: tplId })
            } catch (e) {
                console.error(e)
                this.builderLoading = false
                this.showMessage('Ouverture du configurateur (auto) impossible.', 'error')
            }
        },

        // Alerte
        showMessage(message, type) {
            if (this.alertTimeoutId) {
                clearTimeout(this.alertTimeoutId)
                this.alertTimeoutId = null
            }
            this.alert.message = message
            this.alert.type = type === 'error' ? 'error' : 'success'
            this.alert.show = true
            this.alertTimeoutId = setTimeout(() => {
                this.alert.show = false
                this.alertTimeoutId = null
            }, 30000)
        },
    },
    mounted() {
        this.init()
    },
    watch: {
        documentUrl() {
            console.log('documentUrl changed', this.documentUrl)
        },
    },
}
</script>

<style scoped>
/*
.header {
    position: sticky;
    top: 0;
    right: 0;
    display: flex;
    width: 100%;
    justify-content: space-between;
    align-items: center;
    padding: 20px;
    height: 80px;
    background: #fff;
}
.header h1 {
    height: 30px;
    line-height: 55px;
}
*/

.template-selector {
    background-color: #021832;
    display: flex;
    flex-wrap: wrap;
    gap: 16px;
    align-items: center;
    justify-content: space-between;
    color: #fff;
}
.selector-label,
.selector-dropdown,
.selector-button {
    flex: 1 1 30%;
}
.selector-dropdown {
    border: 1px solid #fff;
    border-radius: 2px;
}
select {
    width: 100%;
    padding: 8px;
    font-size: clamp(12px, 0.729545vw, 16px);
}
.btn-validate {
    padding: 8px 16px;
    font-size: clamp(12px, 0.729545vw, 16px);
    background-color: #2e7d32;
    color: white;
    border: none;
    cursor: pointer;
    border-radius: 4px;
}
.btn-validate:disabled {
    opacity: 0.6;
    cursor: not-allowed;
}
.btn-save {
    padding: 8px 16px;
    font-size: clamp(12px, 0.729545vw, 16px);
    background-color: #1976d2;
    color: white;
    border: none;
    cursor: pointer;
    border-radius: 4px;
}
.btn-signature {
    padding: 8px 16px;
    font-size: clamp(12px, 0.729545vw, 16px);
    background-color: #d27019;
    color: white;
    border: none;
    cursor: pointer;
    border-radius: 4px;
}
.custom-select-wrapper {
    position: relative;
    width: 100%;
}
.custom-select {
    appearance: none;
    -webkit-appearance: none;
    -moz-appearance: none;
    padding: 8px 40px 8px 12px;
    width: 100%;
    font-size: clamp(12px, 0.729545vw, 16px);
    border: 1px solid #ccc;
    border-radius: 4px;
    background-image: url("data:image/svg+xml;charset=UTF-8,%3Csvg viewBox='0 0 140 140' xmlns='http://www.w3.org/2000/svg'%3E%3Cpolyline points='20,50 70,100 120,50' fill='none' stroke='%23666' stroke-width='20'/%3E%3C/svg%3E");
    background-repeat: no-repeat;
    background-position: right 12px center;
    background-size: 16px;
    cursor: pointer;
    color: #fff;
}
option {
    color: #000;
}
.v-alert.success {
    background-color: green !important;
}
.v-alert.error {
    background-color: red !important;
}

/* Modales Builder */
.modal-overlay {
    position: fixed;
    inset: 0;
    background: rgba(0, 0, 0, 0.4);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 10000;
}
.modal-card {
    background: #fff;
    border-radius: 8px;
    box-shadow: 0 10px 30px rgba(0, 0, 0, 0.2);
    width: min(1100px, 96vw);
    padding: 12px;
}
.modal-head {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 8px;
}
</style>
